import {
  BarChart3,
  FileText,
  MessageCircle,
  Shield,
  Target,
  Users,
  Wallet,
  Wrench,
  Zap,
} from 'lucide-react';
import {
  computeChecklistScore,
  getPillarScores,
  getReadinessFromBands,
  isScoredBlock,
} from './checklistUtils';

const SALES_PILLAR_ICONS = [FileText, Users, Wrench, BarChart3, Wallet];
const OPERATIONS_BLOCK_ICONS = [Shield, Users, Zap, MessageCircle, Target];

const SALES_SLUG_MAP = {
  Процессы: 'processes',
  Команда: 'team',
  Инструменты: 'tools',
  Контент: 'content',
  Бюджет: 'budget',
};

const OPERATIONS_SLUG_MAP = {
  'Данные и комплаенс': 'data',
  'Владелец и метрика': 'owner',
  'Интеграции и после пилота': 'integrations',
  'Команда и инструменты': 'team',
  'Бюджет и приоритет': 'budget',
};

function slugifyBlockId(title, slugMap, index) {
  return slugMap[title] || `pillar-${index}`;
}

function resolveSummary(resultConfig, readiness) {
  const summaries = resultConfig?.summaries;
  if (!summaries) return '';
  return summaries[readiness.min] ?? summaries[0] ?? '';
}

function buildProgressLabels(readinessBands) {
  return [...readinessBands]
    .sort((a, b) => a.min - b.min)
    .map((band) => band.label);
}

function buildSalesPillars(checklistData, answers, maxPerQuestion) {
  const pillarScores = getPillarScores(checklistData, answers, maxPerQuestion);

  return checklistData.blocks
    .filter((block) => block.scoring !== false)
    .map((block, index) => {
      const key = block.pillar || block.title;
      const pillar = pillarScores[key] || { score: 0, max: 0 };
      const progress = pillar.max > 0 ? Math.round((pillar.score / pillar.max) * 100) : 0;
      return {
        id: slugifyBlockId(block.title, SALES_SLUG_MAP, index),
        label: block.title,
        score: pillar.score,
        progress,
        Icon: SALES_PILLAR_ICONS[index] || FileText,
      };
    });
}

function buildOperationsPillars(checklistData, answers, maxPerQuestion) {
  const pillarScores = getPillarScores(checklistData, answers, maxPerQuestion);
  let iconIndex = 0;

  return checklistData.blocks
    .filter((block) => isScoredBlock(block))
    .map((block, index) => {
      const key = block.pillar || block.title;
      const pillar = pillarScores[key] || { score: 0, max: 0 };
      const progress = pillar.max > 0 ? Math.round((pillar.score / pillar.max) * 100) : 0;
      const Icon = OPERATIONS_BLOCK_ICONS[iconIndex] || FileText;
      iconIndex += 1;
      return {
        id: slugifyBlockId(block.title, OPERATIONS_SLUG_MAP, index),
        label: block.title,
        score: pillar.score,
        progress,
        Icon,
      };
    });
}

/**
 * Преобразует ответы чек-листа в props для ChecklistResultScreen.
 */
export function buildChecklistResultData({
  pageConfig,
  answers,
  checklistData,
  meta,
  readinessBands,
}) {
  const score = answers._score ?? computeChecklistScore(checklistData, answers);
  const readiness = getReadinessFromBands(score, readinessBands);
  const maxPerQuestion = meta.scaleMax ?? 2;
  const resultConfig = pageConfig.result;
  const isOperations = pageConfig.id === 'operations';

  const pillars = isOperations
    ? buildOperationsPillars(checklistData, answers, maxPerQuestion)
    : buildSalesPillars(checklistData, answers, maxPerQuestion);

  const topBand = readinessBands.reduce((top, band) => (band.min > top.min ? band : top));

  return {
    title: 'Поздравляем!',
    subtitle: 'Вы прошли чек-лист',
    score,
    maxScore: meta.maxScore,
    readinessLabel: readiness.label,
    readinessEmoji: readiness.emoji,
    readyLabels: [topBand.label],
    summary: resolveSummary(resultConfig, readiness),
    progressPercent: meta.maxScore > 0 ? Math.round((score / meta.maxScore) * 100) : 0,
    progressLabels: buildProgressLabels(readinessBands),
    pillars,
    pillarColumns: pillars.length,
    ctaHeading: resultConfig.ctaHeading ?? resultConfig.ctaTitle,
    ctaSubtext: resultConfig.ctaSubtext ?? resultConfig.ctaText,
    ctaLabel: resultConfig.ctaLabel ?? 'Получить полные результаты и записаться',
  };
}
